import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@mantine/core';
import { IconCalendarPlus } from '@tabler/icons-react';
import { useRidesBookingsService } from '@actschurch/ccms-sdk';
import { ridesKeys } from '@/lib/queryKeys';
import { campusById, useRidesLookups } from '@/lib/lookups';
import { combineDateTime } from '@/lib/format';
import { isNotFoundError } from '@/lib/copy';
import { photoRules, useCopy, visiblePhoto } from '@/lib/settings';
import { Callout } from '@/components/ui/Callout';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { RideTicket, SuccessBurst } from '@/components/flows/RideTicket';
import { carWithColour } from '@/components/flows/LiftCard';
import { downloadCalendarFile } from '@/components/flows/calendar';
import { campusAddressLine } from '@/components/rides/CampusPicker';
import { bookingWhoLabel } from './_shared';
import '@/styles/flows.css';

/** Booking confirmed: when it leaves, the car and plate to look for, where to meet, who's travelling. */
export default function RideConfirmPage() {
  const { bookingId } = useParams();
  const id = Number(bookingId);
  const bookings = useRidesBookingsService();
  const lookups = useRidesLookups();
  const q = useQuery({ queryKey: ridesKeys.bookings.byId(id), queryFn: () => bookings.getById(id), enabled: Number.isFinite(id) && id > 0 });
  const b = q.data;
  const { t, settings } = useCopy();
  const driverPhoto = visiblePhoto(b?.driverPhotoUrl, photoRules(settings).driverToRiders);
  const pickup = campusById(lookups.data, b?.collectionLocationID);

  const addToCalendar = () => {
    if (!b) return;
    const start = combineDateTime(b.date, b.departureTime ?? b.serviceTimeStart);
    if (!start) return;
    downloadCalendarFile({
      uid: `booking-${b.rideBookingID}`,
      title: `Carpool to ${b.eventName}`,
      start,
      minutes: 45,
      location: [`Acts Church ${b.collectionName}`, campusAddressLine(pickup)].filter(Boolean).join(', '),
      description: `Driver: ${b.driverName}. Car: ${carWithColour(b.vehicle)}, ${b.vehicle.plate}. Booking ${b.bookingRef}.`,
    }, `carpool-${b.bookingRef}.ics`);
  };

  return (
    <div className="rides-main rides-flow">
      <div className="rides-celebrate" aria-live="polite">
        {b && <SuccessBurst label="Booked" />}
        <h1 className="rides-title">{b ? 'Your seat is booked' : 'Your booking'}</h1>
        {b && (
          <p className="rides-lede" style={{ marginBottom: 0 }}>
            {b.forMinor && b.childName
              ? t('ride.confirm_lede_child', undefined, { driver: b.driverName, child: b.childName, event: b.eventName })
              : t('ride.confirm_lede', undefined, { driver: b.driverName, event: b.eventName })}
          </p>
        )}
      </div>

      {q.isLoading && <LoadingState label="Loading your booking" count={2} />}
      {q.error && (
        <ErrorState
          title={isNotFoundError(q.error) ? "We couldn't find this booking" : "We couldn't load your booking"}
          message={isNotFoundError(q.error) ? 'It may have been cancelled. Your rides are on Home.' : undefined}
          error={q.error}
          onRetry={isNotFoundError(q.error) ? undefined : () => q.refetch()}
          secondaryAction={<Button component={Link} to="/home" variant="default" fullWidth>Back to Home</Button>}
        />
      )}

      {b && (
        <div className="rides-flow-stack rides-stepbody">
          <RideTicket
            eventName={b.eventName}
            date={b.date}
            departureTime={b.departureTime}
            serviceTimeStart={b.serviceTimeStart}
            collectionName={b.collectionName}
            dropOffName={b.dropOffName}
            status="Confirmed"
            driver={{ name: b.driverName, photoUrl: driverPhoto }}
            vehicle={b.vehicle}
            bigPlate
            meet={{ campus: pickup, campusName: b.collectionName }}
            rows={[
              { k: b.forMinor ? 'Travelling' : "Who's travelling", v: bookingWhoLabel(b) },
              ...(b.forMinor && b.guardianName ? [{ k: 'Parent or guardian', v: b.guardianName }] : []),
            ]}
            bookingRef={b.bookingRef}
          />
          <Callout tone="info">{t('booking.confirmation_note')}</Callout>
          <div className="rides-stack">
            <Button component={Link} to={`/ride/day/${b.rideBookingID}`} fullWidth size="lg">Open ride day</Button>
            <Button variant="default" fullWidth leftSection={<IconCalendarPlus size={20} aria-hidden="true" />} onClick={addToCalendar}>Add to my calendar</Button>
            <Button variant="subtle" component={Link} to="/home" fullWidth>Back to Home</Button>
          </div>
        </div>
      )}
    </div>
  );
}
